import React, { useEffect, useState } from 'react';
import { displayStatus, evaluationPercentage } from '../../lib/evaluationIntegrity';
import { ScoreSummary } from '../../components/EvaluationIntegrity';
import { useNavigate } from 'react-router-dom';
import { Building2, FolderKanban, AlertTriangle, Activity } from 'lucide-react';
import { projectsAPI, evaluationsAPI, usersAPI, clientsAPI } from '../../api/client';
import SemaforoBadge from '../../components/SemaforoBadge';
import StatusBadge from '../../components/StatusBadge';
import { SemaforoDistChart, StatusDistChart } from '../../components/ScoreChart';

function StatCard({ icon: Icon, label, value, borderColor, testid }) {
  return (
    <div className="soem-card p-5 border-t-4" style={{ borderTopColor: borderColor }} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="soem-label mb-1">{label}</p>
          <p className="text-3xl font-black text-soem-navy">{value}</p>
        </div>
        <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-slate-50">
          <Icon size={20} strokeWidth={1.5} style={{ color: borderColor }} />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState({ projects: [], evaluations: [], users: [], clients: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [p, e, u, c] = await Promise.all([
          projectsAPI.list(), evaluationsAPI.list(), usersAPI.list(), clientsAPI.list(),
        ]);
        setData({ projects: p.data.items || [], evaluations: e.data.items || [], users: u.data.items || [], clients: c.data.items || [] });
      } catch (err) { /* silenced in production */ }
      finally { setLoading(false); }
    }
    load();
  }, []);

  const evals = data.evaluations;
  const enRevision = evals.filter(e => e.status === 'en_revision').length;
  const bloqueadas = evals.filter(e => e.is_blocked && e.status === 'en_revision').length;
  const activeClients = data.clients.filter(c => c.is_active).length;
  const activeProjects = data.projects.filter(p => p.status === 'activo').length;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => <div key={i} className="soem-card h-28 animate-pulse bg-slate-100" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard" id="dashboard-global">
      <div>
        <h1 className="text-2xl font-bold text-soem-navy">Dashboard Global</h1>
        <p className="text-slate-500 text-sm mt-1">Resumen del estado del sistema</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={FolderKanban} label="Proyectos Activos" value={activeProjects} borderColor="#1A56DB" testid="stat-active-projects" />
        <StatCard icon={Activity} label="En Revisión" value={enRevision} borderColor="#D97706" testid="stat-in-review" />
        <StatCard icon={Building2} label="Clientes Activos" value={activeClients} borderColor="#15803D" testid="stat-active-clients" />
        <StatCard icon={AlertTriangle} label="Evaluaciones Bloqueadas" value={bloqueadas} borderColor="#DC2626" testid="stat-blocked" />
      </div>

      {/* Charts row */}
      {evals.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="soem-card p-5">
            <p className="font-bold text-soem-navy mb-1">Evaluaciones por Nivel de Riesgo</p>
            <p className="text-slate-400 text-xs mb-3">{evals.length} evaluaciones en total</p>
            <SemaforoDistChart evaluations={evals} />
          </div>
          <div className="soem-card p-5">
            <p className="font-bold text-soem-navy mb-1">Evaluaciones por Estado</p>
            <p className="text-slate-400 text-xs mb-3">Distribución actual</p>
            <StatusDistChart evaluations={evals} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Projects */}
        <div className="soem-card" data-testid="projects-list-card">
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <h2 className="font-bold text-soem-navy">Todos los Proyectos</h2>
            <button onClick={() => navigate('/dashboard/proyectos')} className="text-soem-primary text-xs font-semibold hover:underline">Ver todos</button>
          </div>
          <div className="divide-y divide-slate-50">
            {data.projects.length === 0 ? (
              <p className="text-slate-400 text-sm p-5">No hay proyectos registrados.</p>
            ) : (
              data.projects.slice(0, 5).map(p => (
                <div key={p.id} className="flex items-center justify-between px-5 py-3 hover:bg-blue-50/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/dashboard/proyectos/${p.id}`)} data-testid={`project-row-${p.id}`}>
                  <div>
                    <p className="font-semibold text-soem-navy text-sm">{p.name}</p>
                    <p className="text-slate-400 text-xs">ID: {p.id.slice(0, 8)}...</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.status === 'activo' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{p.status}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Evaluations */}
        <div className="soem-card" data-testid="evaluations-list-card">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-soem-navy">Evaluaciones Recientes</h2>
            <span className="text-slate-400 text-xs">{evals.length} total</span>
          </div>
          <div className="divide-y divide-slate-50">
            {evals.length === 0 ? (
              <p className="text-slate-400 text-sm p-5">No hay evaluaciones.</p>
            ) : (
              evals.slice(0, 6).map(e => (
                <div key={e.id}
                  className="flex items-center justify-between px-5 py-3 hover:bg-blue-50/50 cursor-pointer transition-colors"
                  onClick={() => navigate(e.status === 'en_revision' ? `/dashboard/evaluaciones/${e.id}/revision` : `/dashboard/evaluaciones/${e.id}/checklist`)}
                  data-testid={`eval-row-${e.id}`}>
                  <div>
                    <p className="font-semibold text-soem-navy text-sm">{e.general_fields?.instalacion || 'Sin nombre'} <span className="font-mono text-slate-400 text-xs">v{e.version}</span></p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <StatusBadge status={displayStatus(e)} />
                      <ScoreSummary evaluation={e} />
                  </div>
                  </div>
                  <SemaforoBadge semaforo={e.semaforo} percentage={evaluationPercentage(e)} size="sm" />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
